import {
  Block,
  BlockTitle,
  Button,
  Chip,
  List,
  ListItem,
  Page,
  Preloader,
  Stepper,
  Notification,
} from 'konsta/react';
import { useParams } from 'react-router-dom';
import TabBar from '../components/TabBar';
import { useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { InvoiceResultData, Product } from '../models/Models';
import { formatUSD } from '../utils';
import { MdError, MdOutlineShoppingCartCheckout } from 'react-icons/md';
import { Post } from '../helpers/AxiosHelper';
import { hapticFeedback } from '@telegram-apps/sdk';
import { mixTrack } from '../helpers/MixPanelHelper';

const ProductPage = () => {
  const { productId, userProductId } = useParams();
  const { products, userProducts } = useAppContext();
  const [product, setProduct] = useState<Product>();
  const [notFound, setNotFound] = useState(false);
  const [ownProduct, setOwnProduct] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState();
  const [notificationFull, setNotificationFull] = useState(false);

  const [quantity, setQuantity] = useState(1);
  const increase = () => {
    if (quantity + 1 > 5) {
      setQuantity(5);
    } else {
      setQuantity(quantity + 1);
    }
  };
  const decrease = () => {
    setQuantity(quantity - 1 < 1 ? 1 : quantity - 1);
  };

  useEffect(() => {
    if (isNaN(Number(productId))) {
      setNotFound(true);
      return;
    }
    if (products) {
      const userProductIdNum = Number(userProductId);
      const product = products.filter(
        (c) =>
          c.id === Number(productId) &&
          (isNaN(userProductIdNum) ||
            (!isNaN(userProductIdNum) && c.userProductId == userProductIdNum)),
      )[0];
      if (!product) {
        setNotFound(true);
        return;
      }

      if (
        (userProducts?.filter((c) => c.id === userProductIdNum).length ?? 0) > 0
      ) {
        setOwnProduct(true);
      }

      setProduct(product);
    }
  }, [products, userProducts]);

  async function createOrder() {
    if (!product || ownProduct) {
      return;
    }

    setCheckoutLoading(true);

    if (!product.isUsed) {
      const invoiceResult = await Post<
        InvoiceResultData,
        { productId: number; quantity: number }
      >('/api/createOrder', {
        productId: product!.id,
        quantity: quantity,
      });

      if (invoiceResult.error) {
        setError(invoiceResult.error);
        setNotificationFull(true);
        setTimeout(() => {
          setNotificationFull(false);
        }, 5000);
        setCheckoutLoading(false);
        return;
      }

      const invoice = invoiceResult as InvoiceResultData;

      window.location.href = invoice.invoice_url;
    } else {
      const invoiceResult = await Post<
        InvoiceResultData,
        { userProductId: number }
      >('/api/buyUserProduct', {
        userProductId: product!.userProductId,
      });

      if (invoiceResult.error) {
        setError(invoiceResult.error);
        setNotificationFull(true);
        setTimeout(() => {
          setNotificationFull(false);
        }, 5000);
        setCheckoutLoading(false);
        return;
      }

      const invoice = invoiceResult as InvoiceResultData;

      window.location.href = invoice.invoice_url;
    }
  }

  return (
    <Page className="select-none pb-40">
      {notFound && (
        <Block className="text-center text-lg">Product not found!</Block>
      )}

      {!notFound && product && (
        <>
          <Notification
            opened={notificationFull}
            icon={<MdError className="text-red-500" />}
            title="Error"
            subtitle="Could not submit withdraw request."
            text={error}
          />

          <Block>
            <h1 className="currency text-3xl tracking-widest">
              {product.name}
            </h1>
            <div className="flex justify-between items-center  mt-3">
              <h1 className="text-primary text-2xl currency">
                {formatUSD(product.price)}
              </h1>
              <div>
                {!product.isUsed && product.isAvailable && (
                  <Stepper
                    value={quantity}
                    onPlus={increase}
                    onMinus={decrease}
                    small
                    className="mt-1"
                  />
                )}
                {product.isUsed && (
                  <Chip
                    colors={{
                      fillBgMaterial: 'bg-yellow-500',
                      fillTextMaterial: 'text-black',
                    }}
                  >
                    Pre-Owned
                  </Chip>
                )}
              </div>
            </div>
          </Block>

          {product.description && product.description.length > 0 && (
            <>
              <BlockTitle>Description</BlockTitle>
              <Block strong>
                <p>{product.description}</p>

                {product.isUsed && (
                  <p className="text-yellow-500 mt-3">
                    Used miners are sold by MYNR users and offer the same
                    performance and quality as new ones. Get a great deal while
                    enjoying the full power of MYNR mining! 💰⚡
                  </p>
                )}
              </Block>
            </>
          )}

          <BlockTitle>Details</BlockTitle>
          <List strong>
            <ListItem
              title="Avg. Income/Day/Miner"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(product.avgProfitDay)}
                </span>
              }
            />
            <ListItem
              title="Setup Fee"
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(product.setupFee)}
                </span>
              }
            />
            <ListItem
              title="Setup"
              after={
                !product.isUsed ? (
                  <span>{product.setupTimeHours} hours</span>
                ) : (
                  <span>Immediate Transfer</span>
                )
              }
            />
          </List>

          <BlockTitle>Checkout Summary</BlockTitle>
          <List strong>
            <ListItem
              title={`${quantity}X ${product.name.substring(0, 50)}`}
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(product.price * quantity)}
                </span>
              }
            />
            {!product.isUsed && (
              <ListItem
                title={`${quantity}X Setup Fee`}
                after={
                  <span className="currency text-primary tracking-widest">
                    {formatUSD(product.setupFee * quantity)}
                  </span>
                }
              />
            )}
            <ListItem
              title={`Total`}
              after={
                <span className="currency text-primary tracking-widest">
                  {formatUSD(
                    product.price * quantity + product.setupFee * quantity,
                  )}
                </span>
              }
            />
          </List>
          <Block>
            * Tax is included. <br />* The transaction fee is not included and
            will be added to the total amount on the next page.
          </Block>
          <Block>
            {!ownProduct && (
              <Button
                disabled={!product.isAvailable}
                rounded
                onClick={() => {
                  mixTrack('[Click] Checkout', {
                    productId: product.id,
                    name: product.name,
                    price: product.price,
                    isUsed: product.isUsed,
                  });

                  if (hapticFeedback.impactOccurred.isAvailable()) {
                    hapticFeedback.impactOccurred('medium');
                  }

                  createOrder();
                }}
              >
                {!checkoutLoading && (
                  <>
                    Checkout <MdOutlineShoppingCartCheckout className="ms-1" />
                  </>
                )}
                {checkoutLoading && (
                  <div className="text-center">
                    <Preloader
                      size="w-4 h-4"
                      colors={{ iconMaterial: 'text-black' }}
                    />
                  </div>
                )}
              </Button>
            )}

            {ownProduct && (
              <span className="text-yellow-500">
                This product has been listed by you
              </span>
            )}
          </Block>
          <br />
          <br />
          <br />
          <br />
        </>
      )}

      <TabBar selectedTab="store" />
    </Page>
  );
};

export default ProductPage;
